// Centralized banned phrases and templates for ZEN AF content generation
// These are overused phrases that should be avoided to ensure originality

export interface BannedPhrasesConfig {
  overusedPhrases: string[];
  templateStarters: string[];
  genericInsults: string[];
  repetitiveMetaphors: string[];
  mandatoryElements: string[];
}

export const zenAfBannedPhrases: BannedPhrasesConfig = {
  // Frequently repeated curse combinations
  overusedPhrases: [
    'unpaid intern',
    'dickweasel',
    'ass hat',
    'glittery ass hat',
    'discount fog machine',
    'soggy biscuit',
    'drunk penguin',
    'judging cat',
    'disappointed cat who caught you masturbating',
    'WiFi connection having a breakdown',
    'Satan\'s intern',
    'caffeinated hamster',
    'premium grade-A organic',
    'artisanal horseshit',
    'small-batch fuckery',
    'clusterfuck buffet',
    'expired coupon'
  ],

  // Template sentence starters that appear too frequently
  templateStarters: [
    'This is absolutely fucking ridiculous',
    'Tell that bullshit to take a permanent vacation',
    'Your boss is being a total asshole',
    'That meeting sounds like a complete clusterfuck',
    'This situation is genuinely shitty',
    'Family drama can go straight to hell',
    'Tell that anxiety to fuck right off',
    'Fuck that noise, you deserve better',
    'Life is serving you some premium bullshit'
  ],

  // Generic insults used instead of personalized ones
  genericInsults: [
    'Karen',
    'Kevin',
    'Chad',
    'Susan from accounting',
    'that guy from HR',
    'your annoying coworker',
    'toxic boss',
    'difficult family member'
  ],

  // Overused metaphors and comparisons
  repetitiveMetaphors: [
    'like a broken coffee machine',
    'judging you harder than a vegan at a BBQ',
    'side-eyeing like a fed-up hamster',
    'having a breakdown like old WiFi',
    'dysfunction flows like',
    'toxic as a gas station sushi',
    'more dramatic than a soap opera'
  ],

  // Elements that were previously mandatory but should be varied
  mandatoryElements: [
    'motherfucker ending every sentence',
    'forced specific regional language',
    'compound insults with animals',
    'workplace dysfunction references without personalization',
    'generic relationship complaints'
  ]
};

// Validation function to check if content contains banned phrases
export const validateAgainstBannedPhrases = (content: string): {
  hasBannedPhrases: boolean;
  foundPhrases: string[];
  suggestions: string[];
  originalityScore: number;
} => {
  const contentLower = content.toLowerCase();
  const foundPhrases: string[] = [];
  
  // Check all banned phrase categories
  const allBannedPhrases = [
    ...zenAfBannedPhrases.overusedPhrases,
    ...zenAfBannedPhrases.templateStarters,
    ...zenAfBannedPhrases.genericInsults,
    ...zenAfBannedPhrases.repetitiveMetaphors
  ];
  
  for (const phrase of allBannedPhrases) {
    if (contentLower.includes(phrase.toLowerCase())) {
      foundPhrases.push(phrase);
    }
  }
  
  // Calculate originality score (0-100)
  const originalityScore = Math.max(0, 100 - (foundPhrases.length * 10));
  
  const suggestions = foundPhrases.length > 0 ? [
    'Replace generic phrases with user-specific biographical details',
    'Create original compound insults using their actual life circumstances',
    'Use their real toxic people instead of generic "Karen" examples', 
    'Build metaphors from their actual work, relationships, and living situation',
    'Generate fresh therapeutic profanity combinations',
    'Target their specific frustrations, not generic life complaints'
  ] : [
    'Excellent originality - no banned phrases detected!'
  ];
  
  return {
    hasBannedPhrases: foundPhrases.length > 0,
    foundPhrases,
    suggestions,
    originalityScore
  };
};

// Instructions for avoiding template content
export const getAntiTemplateInstructions = (): string => {
  return `
ANTI-TEMPLATE ENFORCEMENT - BANNED PHRASES AND PATTERNS:
- NEVER USE: ${zenAfBannedPhrases.overusedPhrases.slice(0, 8).join(', ')}, or similar overused combinations
- AVOID GENERIC INSULTS: Replace "Karen," "Chad," etc. with their actual difficult people
- NO TEMPLATE STARTERS: Don't begin with "This is absolutely fucking ridiculous" - create fresh openings
- ORIGINAL METAPHORS ONLY: Use their biography for metaphors, not generic "broken coffee machine" comparisons
- PERSONALIZED LANGUAGE: Target their specific situation with natural, authentic expression
- FRESH COMPOUND INSULTS: Combine their actual life details with creative profanity
- BIOGRAPHICAL INTEGRATION: Every creative element must come from their real circumstances
- SUSTAINED ORIGINALITY: Build 6-8 sentence rants using their unique biographical context`;
};